import { Button } from "@frontend/components/ui/button"
import { Kbd } from "@frontend/components/ui/kbd"
import { Slider } from "@frontend/components/ui/slider"
import { Switch } from "@frontend/components/ui/switch"
import { ToggleGroup, ToggleGroupItem } from "@frontend/components/ui/toggle-group"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@frontend/components/ui/tooltip"
import { EditRegionsCard } from "@frontend/pages/case-workspace/pathology/components/edit-regions-card"
import type { EditRegionItem } from "@frontend/pages/case-workspace/pathology/lib/module-edit-regions"
import type { LabelRasterSchema, LabelRasterTool } from "@frontend/pages/case-workspace/pathology/types"
import { BACKGROUND_CLASS_ID, isLabelRasterClassSelectable } from "@shared/extensions/label-raster/schema"
import type { LabelRasterToolBase } from "@virdx/wsi-viewer"
import { AlertTriangle, Brush, Eraser, Eye, EyeOff, Minus, PaintBucket, Pipette, Plus, RotateCcw } from "lucide-react"
import { useMemo, useState } from "react"

import {
  BRUSH_RADIUS_SLIDER_STEPS,
  brushRadiusToSliderPosition,
  clampBrushRadius,
  sliderPositionToBrushRadius,
  stepBrushRadius
} from "../lib/brush-radius-scale"
import { isLabelRasterToolItemPressed, nextLabelRasterTool } from "../lib/label-raster-tool-model"
import { labelTitle } from "../lib/label-title"
import type { OverlayPanelLabel } from "./overlay-panel"

/**
 * Tool row model: Brush / Eraser choose what is written, Fill is a modifier that
 * applies the chosen one to a whole connected region, Pick stands alone. The
 * combinations map onto the viewer's `fill` (brush + fill) and `erase_fill`
 * (eraser + fill) tool states.
 */
const LABEL_RASTER_TOOL_ITEMS = [
  {
    value: "brush",
    icon: Brush,
    tip: "Brush (B) — paints the selected class over background / unannotated only (with Background selected: over unannotated only; turn on Overwrite to turn another class into background)",
    overwriteTip: "Brush (B) — paints the selected class over any class (Overwrite on)"
  },
  {
    value: "erase",
    icon: Eraser,
    tip: "Eraser (E) — marks only the selected class as unannotated",
    overwriteTip: "Eraser (E) — marks any class under the cursor as unannotated (Overwrite on)"
  },
  {
    value: "fill",
    icon: PaintBucket,
    tip: "Fill (F) — applies the brush or eraser to a whole connected region with one click, limited to the area visible in the viewer: brush fills the connected background / unannotated region with the selected class; eraser marks the connected region of the selected class as unannotated",
    overwriteTip:
      "Fill (F) — applies the brush or eraser to a whole connected region with one click, limited to the area visible in the viewer: brush replaces the connected region of the clicked class; eraser removes the connected region of any class (cluster eraser, Overwrite on)"
  },
  { value: "pick", icon: Pipette, tip: "Pick (I) — select the class under the cursor", overwriteTip: null }
] as const satisfies ReadonlyArray<{
  value: LabelRasterToolBase | "fill"
  icon: unknown
  tip: string
  overwriteTip: string | null
}>

const OVERLAY_OPACITY_TIP = "Overlay opacity for all visible classes. Hold Space to peek at the tissue."

const OVERWRITE_OFF_TIP =
  "Overwrite (O) — off: the brush paints only over background / unannotated pixels and the eraser marks only the selected class as unannotated. Turn on to paint over or erase any class."
const OVERWRITE_ON_TIP =
  "Overwrite (O) — on: the brush paints the selected class over any class and the eraser marks any class as unannotated. Turn off to protect existing labels."

export interface LabelRasterModulePanelProps {
  schema: LabelRasterSchema
  /** Resolved overlay series this module edits, or null when the case has none for the domain. */
  targetOverlay: { id: string; label: string; seriesIdentifier?: string } | null
  /** Labels (classes) from the mask's palette; null while loading. */
  labels: OverlayPanelLabel[] | null
  /** Label id the eraser writes; the only palette entry hidden from the class list (background is always listed). */
  unannotatedId: number
  /** Per-class opacity on the overlay; a class is hidden when its value is 0. Opacity itself is global. */
  labelOpacities: Record<number, number>
  onToggleClassHidden: (classId: number) => void
  tool: LabelRasterTool | null
  onToolChange: (tool: LabelRasterTool | null) => void
  classId: number | null
  onClassChange: (classId: number) => void
  radiusPx: number
  onRadiusChange: (radiusPx: number) => void
  maxRadiusPx: number
  /**
   * Overwrite mode: brush paints over any class, eraser clears any class, fill
   * replaces the clicked class's region. Session / UI setting, off by default.
   */
  overwrite: boolean
  onOverwriteChange: (enabled: boolean) => void
  overlayOpacity: number
  onOverlayOpacityChange: (opacity: number) => void
  stats: { chunks: number; changedPixels: number } | null
  crcMismatchCount: number
  onResetEdits: () => void
  readOnly: boolean
  errorMessage?: string | null
  /**
   * Edit regions of the active slide: editing is confined to them and the rest is
   * dimmed. `null` / empty = the slide has none and the whole overlay is editable
   * (the card is not rendered).
   */
  regions?: EditRegionItem[] | null
  /**
   * Hide the regions' dim / outlines in the viewer so the slide and overlay can
   * be seen unobstructed. Visual only: editing stays confined to the regions.
   */
  regionsHidden?: boolean
  onRegionsHiddenChange?: (hidden: boolean) => void
  onJumpToRegion?: (key: string) => void
  onNextRegion?: () => void
}

function swatchStyle(color?: [number, number, number]) {
  return color ? { backgroundColor: `rgb(${color[0]}, ${color[1]}, ${color[2]})` } : undefined
}

export function LabelRasterModulePanel({
  schema,
  targetOverlay,
  labels,
  unannotatedId,
  labelOpacities,
  onToggleClassHidden,
  tool,
  onToolChange,
  classId,
  onClassChange,
  radiusPx,
  onRadiusChange,
  maxRadiusPx,
  overwrite,
  onOverwriteChange,
  overlayOpacity,
  onOverlayOpacityChange,
  stats,
  crcMismatchCount,
  onResetEdits,
  readOnly,
  errorMessage,
  regions = null,
  regionsHidden = false,
  onRegionsHiddenChange,
  onJumpToRegion,
  onNextRegion
}: LabelRasterModulePanelProps) {
  const [confirmReset, setConfirmReset] = useState(false)

  // Background (0) is always listed and selectable: true slide background is a
  // real class, distinct from "unannotated" (which the eraser writes and is never listed).
  const editableLabels = useMemo(() => {
    if (!labels) return []
    const editableClassIds = schema.editable_class_ids ?? null
    return labels.filter((l) => isLabelRasterClassSelectable(l.value, { unannotatedId, editableClassIds }))
  }, [labels, schema.editable_class_ids, unannotatedId])

  if (!targetOverlay) {
    return (
      <div className="space-y-2 text-[11px] text-muted-foreground">
        <p>
          No <span className="font-medium text-foreground">{schema.domain}</span> overlay is attached to this slide.
        </p>
        <p className="text-[10px]">
          Attach a label zarr series with metadata <code>role=overlay</code>, <code>overlay_of=&lt;slide&gt;</code>,{" "}
          <code>domain={schema.domain}</code> to enable editing.
        </p>
      </div>
    )
  }

  const selected = editableLabels.find((l) => l.value === classId) ?? null
  const clampedMax = Math.max(1, Math.floor(maxRadiusPx))
  const clampedRadius = clampBrushRadius(radiusPx, maxRadiusPx)

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4">
      <div className="space-y-1">
        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Editing</div>
        <div className="text-[11px] truncate" title={targetOverlay.seriesIdentifier ?? targetOverlay.label}>
          {targetOverlay.label}
        </div>
        <div className="flex items-center gap-2" title={OVERLAY_OPACITY_TIP}>
          <span className="text-[10px] text-muted-foreground w-14 shrink-0">Opacity</span>
          <Slider
            className="flex-1"
            min={0}
            max={100}
            value={[Math.round(overlayOpacity * 100)]}
            onValueChange={(value) => {
              const vals = Array.isArray(value) ? value : [value]
              onOverlayOpacityChange((vals[0] ?? 0) / 100)
            }}
            aria-label="Overlay opacity"
          />
          <span className="text-[10px] text-muted-foreground w-7 text-right">{Math.round(overlayOpacity * 100)}%</span>
        </div>
      </div>

      {errorMessage && (
        <div className="flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-[10px] text-destructive">
          <AlertTriangle className="size-3 shrink-0 mt-[1px]" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Tool</div>
        <TooltipProvider>
          <ToggleGroup variant="outline" size="sm" className="w-full">
            {LABEL_RASTER_TOOL_ITEMS.map(({ value, icon: Icon, tip, overwriteTip }) => (
              <Tooltip key={value}>
                <TooltipTrigger render={<span className="flex-1" />}>
                  <ToggleGroupItem
                    value={value}
                    aria-label={tip}
                    pressed={isLabelRasterToolItemPressed(value, tool)}
                    onPressedChange={() => onToolChange(nextLabelRasterTool(value, tool))}
                    disabled={readOnly}
                    className={`h-7 w-full p-0 ${
                      isLabelRasterToolItemPressed(value, tool)
                        ? "border-primary/70 bg-primary/20 text-primary aria-pressed:bg-primary/20"
                        : ""
                    }`}
                  >
                    <Icon className="size-3.5" />
                  </ToggleGroupItem>
                </TooltipTrigger>
                <TooltipContent side="bottom">{overwrite && overwriteTip ? overwriteTip : tip}</TooltipContent>
              </Tooltip>
            ))}
          </ToggleGroup>
        </TooltipProvider>
        <div
          className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 ${
            overwrite ? "border-amber-500/60 bg-amber-500/10" : "border-border bg-surface-raised"
          }`}
          data-testid="overwrite-toggle-row"
          title={overwrite ? OVERWRITE_ON_TIP : OVERWRITE_OFF_TIP}
        >
          <button
            type="button"
            className="flex items-center gap-1.5 text-[10px] text-foreground select-none disabled:opacity-60"
            disabled={readOnly}
            onClick={() => onOverwriteChange(!overwrite)}
          >
            <span className="font-medium">Overwrite</span>
            <Kbd>O</Kbd>
            {overwrite && <span className="text-amber-600 dark:text-amber-400">on</span>}
          </button>
          <Switch
            size="sm"
            aria-label="Overwrite mode (O)"
            checked={overwrite}
            onCheckedChange={(checked) => onOverwriteChange(checked)}
            disabled={readOnly}
          />
        </div>
        {(tool === "brush" || tool === "erase") && (
          <div className="space-y-1.5 rounded-md border border-border bg-surface-raised px-2.5 py-2">
            <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
              <span>{tool === "brush" ? "Brush" : "Eraser"} radius</span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="icon-xs"
                  variant="outline"
                  aria-label="Decrease radius by 1 px"
                  title="Decrease radius by 1 px"
                  disabled={readOnly || clampedRadius <= 1}
                  onClick={() => onRadiusChange(stepBrushRadius(clampedRadius, -1, maxRadiusPx))}
                >
                  <Minus className="size-3" />
                </Button>
                <span
                  className="tabular-nums text-foreground min-w-[4.5ch] text-center"
                  data-testid="brush-radius-readout"
                >
                  {clampedRadius} px
                </span>
                <Button
                  type="button"
                  size="icon-xs"
                  variant="outline"
                  aria-label="Increase radius by 1 px"
                  title="Increase radius by 1 px"
                  disabled={readOnly || clampedRadius >= clampedMax}
                  onClick={() => onRadiusChange(stepBrushRadius(clampedRadius, 1, maxRadiusPx))}
                >
                  <Plus className="size-3" />
                </Button>
              </div>
            </div>
            <Slider
              min={0}
              max={BRUSH_RADIUS_SLIDER_STEPS}
              value={[brushRadiusToSliderPosition(clampedRadius, maxRadiusPx)]}
              onValueChange={(value) => {
                const vals = Array.isArray(value) ? value : [value]
                const next = sliderPositionToBrushRadius(vals[0] ?? 0, maxRadiusPx)
                if (next !== clampedRadius) onRadiusChange(next)
              }}
              aria-label={`${tool === "brush" ? "Brush" : "Eraser"} radius`}
              disabled={readOnly}
            />
          </div>
        )}
      </div>

      <EditRegionsCard
        regions={regions}
        hidden={regionsHidden}
        onHiddenChange={onRegionsHiddenChange}
        onJumpToRegion={onJumpToRegion}
        onNextRegion={onNextRegion}
      />

      <div className="flex flex-1 min-h-0 flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Class</div>
          {selected && (
            <span className="text-[10px] text-muted-foreground truncate max-w-[60%]" title={selected.name}>
              {selected.name ?? selected.value}
            </span>
          )}
        </div>
        {labels === null ? (
          <div className="text-[11px] text-muted-foreground">Loading mask palette…</div>
        ) : editableLabels.length === 0 ? (
          <div className="text-[11px] text-muted-foreground">No editable classes.</div>
        ) : (
          <div className="flex-1 min-h-32 overflow-auto space-y-0.5 pr-0.5" data-testid="class-list">
            {editableLabels.map((label) => {
              const isSelected = label.value === classId
              const isBackground = label.value === BACKGROUND_CLASS_ID
              // Background always renders transparent, so it has no visibility toggle.
              const hidden = !isBackground && (labelOpacities[label.value] ?? 1) <= 0
              const displayName = label.name ?? `label ${label.value}`
              const title = isBackground ? `${labelTitle(label)} — renders transparent` : labelTitle(label)
              return (
                <div
                  key={label.value}
                  className={`flex items-center gap-1.5 rounded-md border px-1.5 py-1 ${
                    isSelected ? "border-primary/60 bg-primary/10" : "border-transparent hover:bg-accent/40"
                  } ${hidden ? "opacity-60" : ""}`}
                  data-testid={isBackground ? "class-row-background" : undefined}
                >
                  {isBackground ? (
                    <span className="size-4 shrink-0" aria-hidden="true" />
                  ) : (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground p-0.5 rounded shrink-0"
                      title={hidden ? "Show class (overlay opacity applies to all visible classes)" : "Hide class"}
                      aria-label={`${hidden ? "Show" : "Hide"} ${displayName}`}
                      aria-pressed={hidden}
                      onClick={() => onToggleClassHidden(label.value)}
                    >
                      {hidden ? <EyeOff className="size-3" /> : <Eye className="size-3" />}
                    </button>
                  )}
                  <button
                    type="button"
                    className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
                    disabled={readOnly}
                    onClick={() => {
                      onClassChange(label.value)
                      if (!tool) onToolChange("brush")
                    }}
                  >
                    <span
                      className={`size-3 rounded-sm border shrink-0 ${
                        isBackground ? "border-dashed border-muted-foreground/70 bg-transparent" : "border-border/60"
                      }`}
                      style={isBackground ? undefined : swatchStyle(label.color)}
                    />
                    <span className="text-[11px] truncate" title={title}>
                      {displayName}
                    </span>
                    <span className="text-[9px] text-muted-foreground tabular-nums ml-auto" title={title}>
                      {label.value}
                    </span>
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="shrink-0 space-y-1.5 rounded-md border border-border bg-surface-raised px-2.5 py-2">
        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Edits</div>
        <div className="text-[11px] tabular-nums">
          {stats ? (
            <>
              {stats.chunks} chunk{stats.chunks === 1 ? "" : "s"} · {stats.changedPixels.toLocaleString()} px changed
            </>
          ) : (
            "No edits yet"
          )}
        </div>
        {crcMismatchCount > 0 && (
          <div className="flex items-start gap-1.5 text-[10px] text-amber-600 dark:text-amber-400">
            <AlertTriangle className="size-3 shrink-0 mt-[1px]" />
            <span>
              {crcMismatchCount} chunk{crcMismatchCount === 1 ? "" : "s"} changed in the source since these edits were
              made.
            </span>
          </div>
        )}
        {stats && stats.chunks > 0 && !readOnly && (
          <div className="flex items-center gap-1.5">
            {confirmReset ? (
              <>
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-6 px-2 text-[10px]"
                  onClick={() => {
                    setConfirmReset(false)
                    onResetEdits()
                  }}
                >
                  Discard all edits
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-[10px]"
                  onClick={() => setConfirmReset(false)}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-2 text-[10px]"
                onClick={() => setConfirmReset(true)}
              >
                <RotateCcw className="size-3 mr-1" />
                Reset all edits
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
